(() => {
  if (customElements.get("related-blog-videos")) return;

  const GENERIC_TAGS = new Set([
    "dieselengine",
    "dieselfueleconomy",
    "fueleconomy",
    "suspension",
  ]);

  const PRIORITY_TERMS = [
    "axiom",
    "egr cooler",
    "oil cooler",
    "battery",
    "batteries",
    "truck bed cover",
    "tonneau cover",
    "exhaust system",
    "exhaust kit",
    "lift pump",
    "liftpump",
    "high mileage",
    "duramax",
    "cummins",
    "powerstroke",
    "ecodiesel",
    "gladiator",
    "fass",
    "fleece",
    "apex",
    "baja",
    "edge",
    "carli",
    "kryptonite",
    "red-head",
    "redhead",
    "turbo",
    "injector",
    "cp3",
    "steering",
    "suspension",
  ];

  const VIDEO_INTENTS = [
    {
      id: "egr-cooler",
      terms: ["egr cooler", "egr coolers", "egr"],
      positive: ["egr cooler", "egr coolers", "egr"],
      negative: ["caster", "degree"],
    },
    {
      id: "oil-cooler",
      terms: ["oil cooler", "oil coolers", "engine oil cooler"],
      positive: ["oil cooler", "oil coolers", "engine oil cooler"],
      negative: ["transmission cooler", "intercooler"],
    },
    {
      id: "battery",
      terms: ["truck battery", "diesel battery", "agm battery", "battery", "batteries"],
      positive: ["agm battery", "truck battery", "diesel battery", "battery", "batteries"],
      negative: ["cable", "hold down", "heat shield"],
    },
    {
      id: "bed-cover",
      terms: ["hard truck bed cover", "truck bed cover", "truck bed covers", "bed cover", "bed covers", "tonneau cover", "tonneau covers", "tonneau"],
      positive: ["hard truck bed cover", "truck bed cover", "bed cover", "tonneau cover", "tonneau"],
      negative: ["tailgate", "bed step", "bed mat"],
    },
    {
      id: "exhaust",
      terms: ["exhaust system", "exhaust sound", "exhaust kit", "turbo back", "cat back", "muffler", "downpipe", "exhaust"],
      positive: ["exhaust system", "exhaust systems", "exhaust kit", "exhaust kits", "muffler", "downpipe", "exhaust"],
      negative: ["def", "diesel exhaust fluid", "exhaust brake", "exhaust manifold"],
    },
    {
      id: "lift-pump",
      terms: ["lift pump", "liftpump", "fuel lift pump", "fass", "airdog"],
      positive: ["lift pump", "liftpump", "fass", "airdog"],
      negative: [],
    },
    {
      id: "high-mileage",
      terms: ["high mileage", "high-mileage"],
      positive: ["high mileage", "high-mileage"],
      negative: [],
    },
    {
      id: "transmission",
      terms: ["transmission", "torque converter", "valve body", "68rfe", "allison", "clutch"],
      positive: ["transmission", "torque converter", "valve body", "68rfe", "allison", "clutch"],
      negative: [],
    },
    {
      id: "turbo",
      terms: ["turbocharger", "turbo charger", "vgt", "turbo"],
      positive: ["turbocharger", "turbo charger", "turbo", "vgt"],
      negative: [],
    },
    {
      id: "injector",
      terms: ["fuel injectors", "fuel injector", "injectors", "injector"],
      positive: ["fuel injectors", "fuel injector", "injectors", "injector"],
      negative: [],
    },
    {
      id: "intake",
      terms: ["cold air intake", "air intake", "intake system", "intake"],
      positive: ["cold air intake", "air intake", "intake system"],
      negative: ["intake manifold"],
    },
    {
      id: "suspension",
      terms: ["lift kit", "leveling kit", "shocks", "control arms", "suspension", "track bar", "steering"],
      positive: ["lift kit", "leveling kit", "shocks", "control arms", "suspension", "track bar", "steering"],
      negative: [],
    },
  ];

  class RelatedBlogVideos extends HTMLElement {
    static videoPromise = null;

    connectedCallback() {
      this.articleTitle = this.dataset.articleTitle || "";
      this.articleTags = this.getArticleTags();
      this.articleTagSet = new Set(this.articleTags.map((tag) => this.normalizeTag(tag)));
      this.articleContext = this.getArticleContext();
      this.intents = this.getMatchedIntents();
      this.videosUrl = this.dataset.videosUrl;
      this.container = this.querySelector(".jsContainer");
      this.link = this.querySelector(".jsVideoLink");
      this.image = this.querySelector(".jsVideoImage");
      this.titleElement = this.querySelector(".jsVideoTitle");

      if ((!this.articleTagSet.size && !this.intents.length) || !this.container || !this.link || !this.image || !this.titleElement) return;

      this.loadVideo();
    }

    getArticleTags() {
      const tagScripts = [...this.querySelectorAll("[data-related-video-tags]")];
      if (!tagScripts.length) return [];

      return tagScripts.flatMap((tagScript) => this.parseTags(tagScript.textContent));
    }

    parseTags(value) {
      if (!value) return [];

      try {
        return this.normalizeTagList(JSON.parse(value));
      } catch {
        return this.normalizeTagList(value);
      }
    }

    normalizeTagList(value) {
      if (Array.isArray(value)) {
        return value.flatMap((tag) => this.normalizeTagList(tag));
      }

      return String(value || "")
        .split(/[\n,\u2022]+/)
        .map((tag) => tag.trim())
        .filter(Boolean);
    }

    getArticleContext() {
      const sectionBody = this.closest(".section-body") || document;
      const title = sectionBody.querySelector(".article-card-title")?.textContent || this.articleTitle;
      const content = sectionBody.querySelector(".article-content")?.textContent || "";
      const tags = this.articleTags.join(" ");

      return {
        title: this.normalizeText(title),
        content: this.normalizeText(content),
        tags: this.normalizeText(tags),
      };
    }

    async loadVideo() {
      try {
        const bestVideo = this.getExactVideo() || this.findBestVideo(await this.getVideos());
        if (!bestVideo) return;

        const imageUrl = this.getYouTubeImage(bestVideo.url);
        if (!bestVideo.onlineStoreUrl || !imageUrl) return;

        this.link.href = bestVideo.onlineStoreUrl;
        this.image.src = imageUrl;
        this.image.alt = bestVideo.title;
        this.titleElement.textContent = bestVideo.title;
        this.container.hidden = false;
      } catch (error) {
        console.error("Related blog videos failed to load:", error);
      }
    }

    async getVideos() {
      RelatedBlogVideos.videoPromise = RelatedBlogVideos.videoPromise || this.fetchVideos();
      return RelatedBlogVideos.videoPromise;
    }

    async fetchVideos() {
      if (!this.videosUrl) return this.waitForVideoData();

      try {
        const firstPage = await this.fetchVideoPage(this.videosUrl);
        const pages = Number(firstPage.pages) || 1;
        const videos = Array.isArray(firstPage.videos) ? firstPage.videos : [];

        if (pages > 1) {
          const remainingPages = await Promise.all(
            Array.from({ length: pages - 1 }, (_, index) => this.fetchVideoPage(this.getVideoPageUrl(index + 2)))
          );

          remainingPages.forEach((page) => {
            if (Array.isArray(page.videos)) videos.push(...page.videos);
          });
        }

        return videos;
      } catch {
        return this.waitForVideoData();
      }
    }

    async fetchVideoPage(url) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Related blog video data request failed: ${response.status}`);

      const html = await response.text();
      const content = html.split("<!-- split -->")[1];
      if (!content) throw new Error("Related blog video data response was empty");

      return JSON.parse(content);
    }

    getVideoPageUrl(page) {
      const url = new URL(this.videosUrl, window.location.origin);
      url.searchParams.set("page", page);
      return url.toString();
    }

    getExactVideo() {
      const videoDataScript = this.querySelector("[data-related-video-exact]");
      if (!videoDataScript?.textContent) return null;

      try {
        const video = JSON.parse(videoDataScript.textContent);
        return video && typeof video === "object" ? video : null;
      } catch {
        return null;
      }
    }

    waitForVideoData() {
      if (Array.isArray(window.relatedBlogVideosData)) {
        return Promise.resolve(window.relatedBlogVideosData);
      }

      return new Promise((resolve) => {
        let attempts = 0;
        const check = () => {
          if (Array.isArray(window.relatedBlogVideosData)) {
            resolve(window.relatedBlogVideosData);
            return;
          }

          attempts++;
          if (attempts > 30) {
            resolve([]);
            return;
          }

          setTimeout(check, 100);
        };

        check();
      });
    }

    findBestVideo(videos) {
      return videos
        .map((video) => ({
          video,
          score: this.scoreVideo(video),
          exactTagMatch: this.hasExactTagMatch(video),
        }))
        .filter(({ score, exactTagMatch }) => exactTagMatch || score >= 35)
        .sort((a, b) => {
          if (a.exactTagMatch !== b.exactTagMatch) return a.exactTagMatch ? -1 : 1;
          if (b.score !== a.score) return b.score - a.score;
          return this.getTimestamp(b.video.datePublished) - this.getTimestamp(a.video.datePublished);
        })[0]?.video;
    }

    scoreVideo(video) {
      let score = 0;
      const videoText = this.normalizeText(`${video.title || ""} ${video.handle || ""} ${this.normalizeTagList(video.relatedContentTags).join(" ")}`);

      for (const tag of this.normalizeTagList(video.relatedContentTags)) {
        const normalizedTag = this.normalizeTag(tag);
        if (!this.articleTagSet.has(normalizedTag)) continue;

        score += GENERIC_TAGS.has(normalizedTag) ? 8 : 45;
      }

      for (const intent of this.intents) {
        let intentScore = 0;

        for (const term of intent.positive) {
          if (this.hasPhrase(videoText, term)) intentScore += intent.titleScore > 0 ? 30 : 14;
        }

        for (const term of intent.terms) {
          if (this.hasPhrase(videoText, term)) intentScore += intent.titleScore > 0 ? 10 : 5;
        }

        for (const term of intent.negative) {
          if (this.hasPhrase(videoText, term)) intentScore -= 24;
        }

        score += Math.max(intentScore, -30);
      }

      if (!score) return 0;

      const articleTitle = this.articleContext.title;

      for (const term of PRIORITY_TERMS) {
        if (this.hasPhrase(articleTitle, term) && this.hasPhrase(videoText, term)) {
          score += 15;
        }
      }

      return score;
    }

    hasExactTagMatch(video) {
      return this.normalizeTagList(video.relatedContentTags).some((tag) => {
        const normalizedTag = this.normalizeTag(tag);
        return normalizedTag && !GENERIC_TAGS.has(normalizedTag) && this.articleTagSet.has(normalizedTag);
      });
    }

    getMatchedIntents() {
      const scoredIntents = VIDEO_INTENTS
        .map((intent) => ({
          ...intent,
          ...this.scoreIntent(intent),
        }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);

      const topScore = scoredIntents[0]?.score || 0;
      if (!topScore) return [];

      const titleIntents = scoredIntents
        .filter(({ titleScore }) => titleScore > 0)
        .sort((a, b) => {
          if (b.titleScore !== a.titleScore) return b.titleScore - a.titleScore;
          return b.score - a.score;
        });
      if (titleIntents.length) {
        return titleIntents.filter(({ titleScore }) => titleScore >= titleIntents[0].titleScore * 0.75).slice(0, 2);
      }

      return scoredIntents.filter(({ score }) => score >= Math.max(3, topScore * 0.65)).slice(0, 3);
    }

    scoreIntent(intent) {
      let titleScore = 0;
      let tagScore = 0;
      let contentScore = 0;

      for (const term of intent.terms) {
        const phraseWeight = this.normalizeText(term).split(" ").length;
        if (this.hasPhrase(this.articleContext.title, term)) titleScore += 30 + Math.max(0, phraseWeight - 1) * 15;
        if (this.hasPhrase(this.articleContext.tags, term)) tagScore += 12;

        const bodyCount = this.countPhrase(this.articleContext.content, term);
        if (bodyCount) contentScore += Math.min(bodyCount * 1.5, 9);
      }

      return {
        titleScore,
        score: titleScore + tagScore + contentScore,
      };
    }

    normalizeTag(value) {
      return String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    }

    normalizeText(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }

    hasPhrase(text, phrase) {
      return this.countPhrase(text, phrase) > 0;
    }

    countPhrase(text, phrase) {
      const normalizedPhrase = this.normalizeText(phrase);
      if (!text || !normalizedPhrase) return 0;

      const escapedPhrase = normalizedPhrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return text.match(new RegExp(`(^|\\s)${escapedPhrase}(?=\\s|$)`, "g"))?.length || 0;
    }

    getYouTubeImage(url) {
      const videoId = this.getYouTubeId(url);
      return videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "";
    }

    getYouTubeId(url) {
      const value = String(url || "");
      const patterns = [
        /youtube\.com\/watch\?v=([^&]+)/,
        /youtube\.com\/embed\/([^?&/]+)/,
        /youtu\.be\/([^?&/]+)/,
      ];

      for (const pattern of patterns) {
        const match = value.match(pattern);
        if (match?.[1]) return match[1];
      }

      return "";
    }

    getTimestamp(value) {
      const timestamp = Date.parse(value || "");
      return Number.isFinite(timestamp) ? timestamp : 0;
    }
  }

  customElements.define("related-blog-videos", RelatedBlogVideos);
})();
